import React, { useState, useEffect, useContext } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, FileText } from 'lucide-react';

const SubmissionHistory = () => {
  const { token } = useContext(AuthContext);
  const [submissions, setSubmissions] = useState([]);
  const [apps, setApps] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchApps();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApps(response.data);
    } catch (error) {
      console.error('Failed to fetch apps');
    }
  };

  const handleView = async (submissionId) => {
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSubmission(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load submission details');
    }
  };

  const getAppName = (appId) => {
    const app = apps.find(a => a.id === appId);
    return app ? `${app.name} / ${app.name_es}` : 'Unknown App';
  };

  const formatDate = (dateString) => {
    return _fmtDateTime(dateString);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'text-blue-600';
      case 'reviewed': return 'text-yellow-600';
      case 'approved': return 'text-green-600';
      case 'archived': return 'text-slate-600';
      default: return 'text-slate-600';
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Submission History / Historial de Envíos
        </h2>
        <p className="text-sm text-slate-600">View your submitted forms / Ver sus formularios enviados</p>
      </div>

      <div className="space-y-3">
        {submissions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No submissions yet.</p>
              <p className="text-slate-500 text-sm">Aún no hay envíos.</p>
            </CardContent>
          </Card>
        ) : (
          submissions.map((submission) => (
            <Card key={submission.id} className="hover:shadow-md transition-shadow" data-testid="submission-history-item">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{getAppName(submission.app_id)}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      {formatDate(submission.submitted_at)}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleView(submission.id)}
                    data-testid="view-submission-history-btn"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Status / Estado:</span>
                  <span className={`text-sm font-medium ${getStatusColor(submission.status)}`}>
                    {submission.status}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submission Details / Detalles del Envío</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">App / Aplicación</Label>
                  <p className="font-medium">{getAppName(selectedSubmission.app_id)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status / Estado</Label>
                  <p className={`font-medium ${getStatusColor(selectedSubmission.status)}`}>
                    {selectedSubmission.status}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Submitted At / Enviado el</Label>
                <p className="font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
              </div>

              <div>
                <Label className="text-xs text-slate-500 mb-2 block">Form Data / Datos del Formulario</Label>
                <div className="space-y-2 bg-slate-50 p-4 rounded-lg">
                  {Object.entries(selectedSubmission.data).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b pb-2">
                      <span className="text-sm font-medium text-slate-600">{key}:</span>
                      <span className="text-sm text-slate-800">{value || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {selectedSubmission.signature && (
                <div>
                  <Label className="text-xs text-slate-500">Signature / Firma</Label>
                  <p className="font-medium italic">{selectedSubmission.signature}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmissionHistory;